import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

export const checkStorageWriteErrorsConfigSchema = z.object({
  slackChannel: z
    .string()
    .prefault("#sentry-all")
    .describe("Slack channel to send alerts to"),
  folderPrefixes: z
    .array(z.string().trim().min(1))
    .default([])
    .describe(
      "Division folder prefixes to check errors for, e.g. CWG/, CWR/. Required — re-seed to populate.",
    ),
  allFolderPrefixes: z
    .array(
      z.object({
        id: z.string(),
        prefix: z.string(),
        enabled: z.boolean(),
      }),
    )
    .optional()
    .describe(
      "UI-oriented folder list that preserves disabled folder prefixes while editing the workflow.",
    ),
});

export type CheckStorageWriteErrorsConfig = z.infer<
  typeof checkStorageWriteErrorsConfigSchema
>;

export const checkStorageWriteErrorsStep = defineStepConfig({
  type: "check_storage_write_errors",
  name: "Check Storage Write Errors",
  description:
    "Scans Azure diagnostic logs for non-2xx blob storage write operations in configured division folders and alerts via Slack.",
  category: "Integration",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({
    scheduledTime: z
      .string()
      .optional()
      .describe("Scheduled fire time from the schedule trigger"),
  }),

  outputSchema: z.object({
    date: z.string().describe("Date scanned (YYYY-MM-DD)"),
    blobsScanned: z.number().describe("Number of log blobs scanned"),
    errorsFound: z.number().describe("Number of non-2xx entries found"),
    slackAlertSent: z.boolean().describe("Whether a Slack alert was sent"),
  }),

  configSchema: checkStorageWriteErrorsConfigSchema,

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
  },
});
